import React, { useMemo, useState } from 'react';
import { Modal, Form } from '@puppet/react-components';
import { useTranslation } from 'react-i18next';
import { UserV1 } from '@puppet/cd4pe-client-ts';
import { checkPasswordForSignup } from 'src/utils/passwordPolicy';
import { useAppDispatch } from '@hooks/redux';
import alerts from '@state/ui/alerts';
import { useUpdateUserV1 } from '@services/cd4pe/users';
import Cd4peError from '@components/Cd4peError';

type Props = {
  isOpen: boolean;
  onClose: (open: boolean) => void;
  user: UserV1;
};

type FormValues = {
  password: string;
  confirmPassword: string;
};

type PdsFormError = {
  message?: string;
  causes?: Array<string | Error | { message?: string; causes?: unknown[] }>;
  items?: Record<string, string>;
};

const ChangePasswordDialog = ({ isOpen, onClose, user }: Props) => {
  const { t } = useTranslation('codeDelivery');
  const appDispatch = useAppDispatch();
  const updateUserPassword = useUpdateUserV1();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formError, setFormError] = useState<PdsFormError | undefined>(
    undefined,
  );

  const policyText = useMemo(
    () => ({
      length: t('signup.form.field.password.policy.length'),
      upper: t('signup.form.field.password.policy.upper'),
      lower: t('signup.form.field.password.policy.lower'),
      number: t('signup.form.field.password.policy.number'),
      special: t('signup.form.field.password.policy.special'),
    }),
    [t],
  );

  const clearErrors = () => setFormError(undefined);

  const handleClose = () => {
    clearErrors();
    onClose(false);
  };

  const onSubmitHandler = ({ password, confirmPassword }: FormValues) => {
    clearErrors();

    const policy = checkPasswordForSignup(password);
    if (!policy.ok) {
      const missingBullets = policy.missing.map((k) => policyText[k]);

      setFormError({
        message: t('signup.form.field.password.policy.summary'),
        causes: missingBullets,
        items: {
          password: t('signup.form.field.password.policy.inline'),
        },
      });
      return;
    }

    if (password !== confirmPassword) {
      setFormError({
        message: t('profile.change.password.dialog.nomatch.error'),
        items: {
          password: t('profile.change.password.dialog.nomatch.error'),
          confirmPassword: t('profile.change.password.dialog.nomatch.error'),
        },
      });
      return;
    }

    updateUserPassword.mutate(
      {
        userId: user.id!,
        requestBody: {
          password,
        },
      },
      {
        onSuccess: () => {
          clearErrors();
          onClose(false);
          appDispatch(
            alerts.actions.createAlert({
              type: 'Success',
              message: t('profile.change.password.dialog.success'),
            }),
          );
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <Modal.Title>{t('profile.change.password.dialog.title')}</Modal.Title>
      <Form
        submittable
        cancellable
        onSubmit={(values: FormValues) => onSubmitHandler(values)}
        submitting={updateUserPassword.isLoading}
        onCancel={handleClose}
        submitLabel={t('profile.change.password.dialog.save.changes.button')}
        cancelLabel={t('profile.change.password.dialog.cancel.button')}
        data-testid="profile-change-password-form"
        error={formError}
      >
        <Form.Field
          name="password"
          type={showPassword ? 'text' : 'password'}
          label={t('profile.change.password.dialog.new.password.label')}
          placeholder={t(
            'profile.change.password.dialog.new.password.placeholder',
          )}
          required
          data-testid="profile-change-password-input"
          trailingButtonIcon={showPassword ? 'private' : 'eye'}
          trailingButtonProps={{
            'aria-label': t(
              `signup.form.field.confirmPassword.toggle.ariaLabel`,
            ),
          }}
          onClickTrailingButton={() => setShowPassword(!showPassword)}
        />
        <Form.Field
          name="confirmPassword"
          type={showConfirmPassword ? 'text' : 'password'}
          label={t('profile.change.password.dialog.confirm.password.label')}
          placeholder={t(
            'profile.change.password.dialog.confirm.password.placeholder',
          )}
          required
          data-testid="profile-confirm-password-input"
          trailingButtonIcon={showConfirmPassword ? 'private' : 'eye'}
          trailingButtonProps={{
            'aria-label': t(
              `signup.form.field.confirmPassword.toggle.ariaLabel`,
            ),
          }}
          onClickTrailingButton={() =>
            setShowConfirmPassword(!showConfirmPassword)
          }
        />
      </Form>
      <Cd4peError error={updateUserPassword.error} />
    </Modal>
  );
};

export default ChangePasswordDialog;
